/**
 * Agent detail + PATCH edit (name, description, styles, capabilities, states).
 *
 * An entity header and labelled bands, not a stack of cards. A page of bands
 * reads as one record with parts; the same content in cards reads as several
 * separate things that happen to be stacked, and the agent is one record.
 *
 * The capability allow-list stays editable here. It is the agent's whole point
 * and the list page has no room for it — dropping it to a count on both
 * surfaces would leave it editable nowhere.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { getAgent, updateAgent } from '@optum/api-client/endpoints/agents';
import { getAdminOrganization } from '@optum/api-client/endpoints/organizations';
import type { AgentLifecycle, AgentRuntime } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { Button } from '../../ui/Button.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Band } from '../../ui/Band.js';
import { RecordShape, ShapeError, ShapeLoading } from '../../ui/shapes.js';
import { Field as FormField } from '../../ui/Field.js';
import { Avatar, Input, Typography } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { CapabilityMultiSelect } from './CapabilityMultiSelect.js';

export function AgentDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const { isPlatformAdmin } = useAuth();
  const query = useQuery({ queryKey: qk.agent(id), queryFn: () => getAgent(id) });
  const orgId = query.data?.org_id ?? '';
  const orgQuery = useQuery({
    queryKey: qk.org(orgId),
    queryFn: () => getAdminOrganization(orgId),
    enabled: orgId.length > 0,
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [styles, setStyles] = useState('');
  const [capabilities, setCapabilities] = useState<string[]>([]);
  const [lifecycle, setLifecycle] = useState<AgentLifecycle>('active');
  const [runtime, setRuntime] = useState<AgentRuntime>('paused');

  useEffect(() => {
    if (!query.data) return;
    setName(query.data.name);
    setDescription(query.data.description);
    setStyles(query.data.styles ?? '');
    setCapabilities(query.data.capabilities);
    setLifecycle(query.data.lifecycle_state);
    setRuntime(query.data.runtime_state);
  }, [query.data]);

  useBreadcrumbTail(query.data?.name);

  const qc = useQueryClient();
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: () =>
      updateAgent(id, {
        name,
        description,
        styles: styles || null,
        capabilities,
        lifecycle_state: lifecycle,
        runtime_state: runtime,
      }),
    onSuccess: (next) => {
      toast.success('Saved');
      qc.setQueryData(qk.agent(id), next);
      qc.invalidateQueries({ queryKey: qk.agents(next.org_id) });
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });

  if (query.isPending) {
    return <ShapeLoading shape="record" />;
  }
  if (query.isError) {
    return <ShapeError shape="record" error={query.error} fallback="Failed to load" />;
  }

  function handleSubmit(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    mutation.mutate();
  }

  const agent = query.data;
  const orgName = orgQuery.data?.name ?? agent.org_id;

  return (
    <RecordShape
      back={{ to: `/orgs/${agent.org_id}/agents`, label: 'Agents' }}
      avatar={<Avatar initials={agent.name.slice(0, 2).toUpperCase()} size={48} />}
      eyebrow={
        <>
          <Typography variant="eyebrow">Agent</Typography>
          <StatusBadge status={agent.runtime_state} />
        </>
      }
      title={agent.name}
      subtitle={
        <Typography variant="meta" mono as="span">
          {`${agent.id} · ${orgName}`}
        </Typography>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        <Band label="Identity" style={{ marginBottom: 11 }} />
        <FormField id="ag-name" label="Name" required>
          <Input
            id="ag-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!isPlatformAdmin}
          />
        </FormField>
        <FormField id="ag-desc" label="Description">
          <Input
            id="ag-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={!isPlatformAdmin}
          />
        </FormField>
        <FormField
          id="ag-styles"
          label="Styles"
          hint="Free-form style hints passed to the agent (tone, formatting, persona). Optional."
        >
          <Input
            id="ag-styles"
            value={styles}
            onChange={(e) => setStyles(e.target.value)}
            disabled={!isPlatformAdmin}
          />
        </FormField>

        <Band
          label="Capabilities"
          meta={`${capabilities.length} attached`}
          style={{ marginTop: 22, marginBottom: 11 }}
        />
        <p style={{ marginBottom: 7, fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
          Which capabilities this agent may run. Nothing executes that is not on this list.
        </p>
        <CapabilityMultiSelect
          value={capabilities}
          onChange={setCapabilities}
          disabled={!isPlatformAdmin}
        />

        <Band label="State" style={{ marginTop: 22, marginBottom: 11 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 11 }}>
          <FormField id="ag-lc" label="Lifecycle">
            <Select
              value={lifecycle}
              onChange={(v) => {
                setLifecycle(v as AgentLifecycle);
              }}
              disabled={!isPlatformAdmin}
              options={[
                { value: 'active', label: 'active' },
                { value: 'inactive', label: 'inactive' },
              ]}
            />
          </FormField>
          <FormField id="ag-rt" label="Runtime">
            <Select
              value={runtime}
              onChange={(v) => {
                setRuntime(v as AgentRuntime);
              }}
              disabled={!isPlatformAdmin}
              options={[
                { value: 'running', label: 'running' },
                { value: 'paused', label: 'paused' },
                { value: 'failed', label: 'failed' },
                { value: 'maintenance', label: 'maintenance' },
              ]}
            />
          </FormField>
        </div>

        {isPlatformAdmin && (
          <div style={{ marginTop: 22, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="primary" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        )}
      </form>
    </RecordShape>
  );
}
